/**
 * ENG-5641 — connectivity-probe strategy resolver.
 *
 * Maps an installed integration to the *simplest read-only reachability probe*
 * for its toolkit, returning a declarative {@link ConnectivityProbeDescriptor}.
 * The descriptor says WHAT kind of probe to run and WHERE it can run; the
 * actual call lives in the executors so this module stays pure, dependency-free
 * and unit-testable, and so provider-specific auth nuances (Linear's raw-key vs
 * Bearer scheme, Composio's user_id binding, the MCP handshake) stay in the one
 * place they're already tested:
 *
 *   - The manager CLI (host-side) interprets every kind — it runs where the
 *     agent's real credentials, network egress and live MCP servers are, so it
 *     can probe all four `toolkit_definitions.source_type`s honestly.
 *   - The API `POST /integrations/:id/test` endpoint interprets only the
 *     centrally-reachable kinds (see {@link ConnectivityProbeDescriptor.centralReachable}).
 *
 * INVARIANT: every probe is read-only / non-mutating. The resolver only ever
 * returns descriptors for cheap reads (list, viewer, --version, tools/list).
 * Executors MUST assert `descriptor.readOnly` before running anything.
 */

import type { IntegrationAuthType } from '../types/integration.js';
import type { ConnectivityCause, ConnectivityEvidence } from './integration-health.js';
import { getOAuthProvider } from './oauth-providers.js';

/** `toolkit_definitions.source_type` (see 20250101000001_init.sql). */
export type ToolkitSourceType = 'managed' | 'mcp_server' | 'cli_tool' | 'native';

export type ConnectivityProbeKind =
  /** Provider-specific read-only HTTP check (Linear viewer, Google userinfo, …). Executor owns the call. */
  | 'http_provider'
  /** Managed/Composio: verify the connected account is ACTIVE and bound to the agent's runtime user_id. */
  | 'composio_account'
  /**
   * Managed/Composio host-side: MCP `tools/list` handshake AND the connected-
   * account binding check, combined (worst signal wins). The handshake catches
   * network / token-injection / MCP-URL drift; the account check catches a
   * dead/mis-bound connection that `tools/list` reads green on (ENG-6139).
   */
  | 'managed_composite'
  /** Remote streamable-HTTP MCP server: `initialize` → `tools/list` handshake over HTTP. */
  | 'mcp_tools_list'
  /**
   * Local STDIO MCP server (origami, xero, augmented-*): the manager spawns the
   * toolkit's bundled server with the agent's env, runs `initialize` →
   * `tools/list`, and (when the toolkit defines a read-only `connectivity_test`
   * tool) `tools/call`s it — the honest host-side reachability + auth check for
   * a stdio server whose live pipes only Claude Code holds (ENG-7405). Host-only.
   */
  | 'mcp_stdio'
  /** Native CLI tool: run a read-only command (e.g. `--version`, `whoami`). */
  | 'cli_command'
  /** Built-in / in-process module: a local reachability check. */
  | 'builtin'
  /**
   * ENG-8316: the host structurally CANNOT probe this integration — a
   * broker-backed toolkit whose credentials are minted per task server-side, so
   * there is no host-reachable endpoint. Distinct from `unsupported`, and the
   * distinction is load-bearing.
   *
   * `unsupported` yields a null outcome, and a null outcome is DROPPED from the
   * report batch entirely (`connectivity-probe-runner.ts`: `if (!outcome) {
   * skipped++; continue; }`). A row that is never reported never reaches
   * `applyConnectivityReports`, which is where the server-side handling for
   * these rows lives — including ENG-8205's self-healing clear of stale
   * connectivity state. That clear runs "on the next report"; for a dropped row
   * there is no next report, ever.
   *
   * That is why cloud-broker / aws-cli / gcloud sat at a frozen
   * `transient_error` from 2026-06-14 (when ENG-6428 short-circuited them to
   * `unsupported`) until this ticket, and why xero-broker reached 495
   * consecutive failures: the remedy written for exactly these rows could not
   * see them.
   *
   * So this kind still performs no network call, but it produces a REAL
   * outcome (`unverified`) so the row stays in the report and the server-side
   * path keeps running on it.
   */
  | 'host_unprobeable'
  /** No connectivity probe is available for this integration. */
  | 'unsupported';

/**
 * Latest single connectivity observation — mirrors the `last_connectivity_status`
 * column (ENG-5641).
 *
 * ENG-8226 added `unverified`: the honest verdict when no live check could run.
 * Before it, a probe with nothing to call had no value to write except `ok`, so
 * the `builtin` strategy returned a hardcoded green having contacted nothing —
 * indistinguishable in the column from Outlook's verified live tool call.
 * `unverified` is NOT a failure and NOT health; it ranks between `ok` and
 * `transient_error` in {@link CONNECTIVITY_SEVERITY} so a real observation of
 * trouble always outranks it, and it always outranks a green.
 */
/**
 * ENG-9122: the runtime list, with the type DERIVED from it rather than
 * declared alongside it.
 *
 * It was a bare type union, so every other layer that needed to enumerate these
 * values re-typed them by hand — and `HOST_PROBE_STATUSES` in
 * `packages/core/src/admin-debug/index.ts` copied four of the five. An
 * `unverified` host verdict therefore matched nothing central, fell through to
 * the generic `probe_error` branch, and was reported to operators as the single
 * character `{`. Eight criticals and six weeks came out of that one omission.
 *
 * A type cannot be iterated at runtime; this array can, so the parity test can
 * assert the central set is a superset and the next value added here cannot
 * silently reopen it.
 */
export const CONNECTIVITY_STATUSES = [
  'ok',
  'unverified',
  'degraded',
  'transient_error',
  'down',
] as const;

export type ConnectivityStatus = (typeof CONNECTIVITY_STATUSES)[number];

export interface ConnectivityProbeDescriptor {
  kind: ConnectivityProbeKind;
  sourceType: ToolkitSourceType;
  /**
   * Always true. Present so executors can assert the invariant and refuse to
   * run any probe that somehow isn't a read.
   */
  readOnly: true;
  /** Human-readable label for logs / UI, e.g. "Linear: viewer query". */
  label: string;
  /**
   * Whether this probe can run from the central control plane (the API), or
   * only host-side in the manager CLI. The manager runs every kind; the API
   * only runs probes where it can reach the provider with the right identity.
   *
   * `mcp_tools_list`, `cli_command` and `builtin` are host-only by design —
   * a central Lambda has neither the agent's stdio MCP process, its shell, nor
   * its exact network/credential position, and probing centrally would report
   * green on something it never actually touched.
   */
  centralReachable: boolean;
  /** For `http_provider`: which provider check the executor should run (the `definition_id`). */
  httpProvider?: string;
  /** For `cli_command`: the read-only args, e.g. `['--version']`. */
  cliArgs?: string[];
  /**
   * ENG-5463 — for `cli_command`: whether {@link cliArgs} is a real
   * CREDENTIAL check (`gh auth status`) rather than the `--version` fallback.
   *
   * This exists because the executor used to grade evidence on `cliArgs`
   * being SET, and `cliArgsFor` never returns undefined — it falls back to
   * `['--version']`. So every CLI probe was graded `live_call`, including the
   * bare version check, directly under a comment stating that a `--version`
   * probe "proves the binary exists, not that its credentials work —
   * `handshake`". The comment described the intended behaviour; the condition
   * next to it could not produce it.
   *
   * That is the ENG-8345 false-green shape: `live_call` is the ONLY evidence
   * grade `evidenceSupportsOk` admits, so a toolkit whose credentials were
   * revoked rendered green on the strength of its binary being installed.
   */
  cliArgsAreAuthCheck?: boolean;
  /**
   * ENG-6212 — for `managed_composite`: an operator-stored OVERRIDE tool to call
   * live (e.g. `GMAIL_GET_PROFILE`) instead of letting the probe auto-pick. This
   * is carried VERBATIM from `toolkit_definitions.connectivity_test.tool`; the
   * authoritative read-only gate is the probe itself (`resolveProbeTool` re-checks
   * it against the live tools/list and falls back to the heuristic on drift /
   * non-read-only), so the pure resolver does not — and cannot — validate it.
   */
  probeTool?: string;
  /** ENG-6212 — args for {@link probeTool} (managed/MCP). Default `{}`. */
  probeArgs?: Record<string, unknown>;
}

export interface ConnectivityProbeOutcome {
  status: ConnectivityStatus;
  message?: string;
  details?: Record<string, unknown>;
  /**
   * ENG-8226 — what this probe actually DID to reach its verdict. Persisted to
   * `last_connectivity_evidence` so an `ok` is self-describing: only a
   * `live_call` justifies a green health verdict. Absent ⇒ callers must treat it
   * as `none` (unknown provenance is not a live call).
   */
  evidence?: ConnectivityEvidence;
  /**
   * ENG-9173 — WHY this probe failed, in the vocabulary the escalation layer
   * needs. Persisted to `last_connectivity_cause`.
   *
   * Sibling to {@link evidence} and added for the same reason: `status` alone is
   * not a self-describing verdict. Evidence made an `ok` legible ("was anything
   * actually called?"); this makes a FAILURE legible ("was it us, the
   * credential, or the vendor refusing service?").
   *
   * Absent ⇒ callers read `unknown`, and `unknown` is treated as ACTIONABLE.
   * Never inferred from the message: guessing a cause at an escalation boundary
   * is how ENG-8355 / ENG-6428 happened in the other direction, where a
   * permanent fault was graded transient and could never escalate at all.
   */
  cause?: ConnectivityCause;
}

/**
 * ENG-8358 — the handshake-only verdict for an `initialize → tools/list` probe.
 *
 * An EMPTY tool manifest is not health. Both MCP probes (the CLI's stdio
 * `probeMcpStdio` and this package's HTTP `probeMcpHttp`) used to return a flat
 * `ok` with the message "0 tools", which is indistinguishable in
 * `last_connectivity_status` from a server advertising a full toolset. That
 * green is load-bearing: an `ok` ZEROES the failure streak
 * (`integration-connectivity-report.ts`, `nextStreak = 0`) and closes any open
 * spike/down alert — so a server that has quietly lost its entire surface can
 * clear its own alarm.
 *
 * The honest answer is `unverified`: the transport and the MCP framing are
 * proven, but zero capability was demonstrated. `unverified` is NEUTRAL to both
 * hysteresis counters (`nextStreak = prevStreak`), so it neither manufactures a
 * false hard-down (the ENG-6428 / ENG-8345 trap that sat integrations at 282 and
 * 439 fake failures) nor lets a dead upstream reset its own streak. This mirrors
 * the established ENG-8226 precedent, where the report layer already "refuses ok"
 * over a known-bad `status_message` and records `unverified` instead.
 *
 * Deliberately NOT `down`: a server legitimately advertising nothing (every tool
 * filtered by an allowlist, a genuinely empty toolkit) would then escalate to a
 * hard-down at streak >= 3 and flip the managed-connection-health status —
 * marking a reachable integration broken. Under-claiming health is recoverable;
 * a false `down` pages a human and downgrades a working integration.
 *
 * Lives here, next to the status union, because BOTH probes must apply the same
 * floor. A floor on one probe only would move the gap rather than close it — the
 * same two-site drift the classification-allowlist parity guard exists to catch.
 *
 * Note this is the HANDSHAKE-only path. A probe that successfully made a
 * read-only `connectivity_test` tools/call has proven execution and keeps its
 * `ok` regardless of manifest size — that leg earns `live_call` evidence.
 */
export function handshakeToolsListOutcome(toolCount: number | undefined): ConnectivityProbeOutcome {
  // `undefined` means the server answered tools/list WITHOUT a readable `tools`
  // array. That is WEAKER evidence than an explicit empty list, not stronger —
  // we never even saw a manifest. It previously reported `ok`/"reachable",
  // which is the same unearned green this function exists to remove.
  if (toolCount === undefined) {
    return {
      status: 'unverified',
      message: 'MCP tools/list returned no readable tool list - handshake succeeded but no capability was proven',
    };
  }
  if (toolCount === 0) {
    return {
      status: 'unverified',
      message: 'MCP server advertised 0 tools - handshake succeeded but no capability was proven',
      details: { toolCount: 0 },
    };
  }
  return {
    status: 'ok',
    message: `${toolCount} tools`,
    details: { toolCount },
  };
}

/**
 * Severity ranking for {@link ConnectivityStatus} (higher = worse).
 *
 * ENG-8226: `unverified` sits just above `ok`. It is not a failure — a real
 * observation of trouble must outrank it — but it must never be mistaken for
 * health, so it can never be folded away by a green.
 */
const CONNECTIVITY_SEVERITY: Record<ConnectivityStatus, number> = {
  ok: 0,
  unverified: 1,
  transient_error: 2,
  degraded: 3,
  down: 4,
};

/**
 * ENG-8226: strength ranking for {@link ConnectivityEvidence} (higher = stronger).
 *
 * Folded with BEST-wins while statuses fold with WORST-wins. Both are correct:
 * a composite probe whose live tool call FAILED still made a live call, so the
 * verdict is `down` and the evidence is `live_call`. Recording the strongest
 * thing we actually did is what makes the verdict auditable.
 */
const EVIDENCE_STRENGTH: Record<ConnectivityEvidence, number> = {
  none: 0,
  record_only: 1,
  handshake: 2,
  live_call: 3,
};

/** Return the stronger of two evidence levels (ENG-8226). */
export function bestConnectivityEvidence(
  a: ConnectivityEvidence,
  b: ConnectivityEvidence,
): ConnectivityEvidence {
  return EVIDENCE_STRENGTH[b] > EVIDENCE_STRENGTH[a] ? b : a;
}

/**
 * Return the more-severe of two probe outcomes (ENG-6139). Used by composite
 * probes (e.g. managed = MCP handshake + connected-account binding) so the
 * worst signal wins — a green handshake never masks a dead/mis-bound account.
 */
export function worseConnectivityOutcome(
  a: ConnectivityProbeOutcome,
  b: ConnectivityProbeOutcome,
): ConnectivityProbeOutcome {
  return CONNECTIVITY_SEVERITY[b.status] > CONNECTIVITY_SEVERITY[a.status] ? b : a;
}

/**
 * ENG-6212 — the shape stored in `toolkit_definitions.connectivity_test`: an
 * optional connectivity-test OVERRIDE. `tool` + `args` for managed/MCP toolkits
 * (call this specific read-only tool); `args` as a string[] for `cli_tool`
 * toolkits (run these read-only CLI args instead of the default `--version`).
 * Null/absent ⇒ the resolver and probe use their existing defaults.
 */
export interface ConnectivityTestOverride {
  /** managed/MCP: the specific tool to call (e.g. `GMAIL_GET_PROFILE`). */
  tool?: string | null;
  /** managed/MCP: an object of args for {@link tool}; cli_tool: a string[] of CLI args. */
  args?: Record<string, unknown> | string[] | null;
}

export interface ConnectivityProbeInput {
  /** Integration `definition_id`, e.g. 'linear', 'composio/gmail'. */
  definitionId: string;
  /** `toolkit_definitions.source_type`, when known. */
  sourceType?: ToolkitSourceType | null;
  /** The integration row's `auth_type`. */
  authType?: IntegrationAuthType | null;
  /**
   * ENG-6212 — the toolkit's `connectivity_test` override, when set. Carried onto
   * the descriptor (managed → probeTool/probeArgs; cli_tool → cliArgs). Null/absent
   * ⇒ heuristic pick (managed) / default `--version` (cli).
   */
  connectivityTest?: ConnectivityTestOverride | null;
  /**
   * ENG-7077 — the catalog `integration_definitions.remote_mcp.url` for a
   * hosted-remote-MCP integration (ADR-0033: monday, peec, anchor-browser).
   *
   * Step 3 below detects a remote MCP by asking `OAUTH_PROVIDERS` for an
   * `mcpUrl`, which is the CODE registry. ADR-0033 moved hosted remote MCPs to
   * the DB catalog, and their toolkits carry `source_type: 'native'` — so they
   * fell past step 3 into the `native` case and resolved to `builtin`, which
   * returns a hardcoded `unverified` having contacted nothing. Every static-token
   * hosted remote MCP was therefore UNPROBED: a token revoked months later kept
   * reporting exactly what a healthy one reported, and `unverified` is neutral to
   * the hysteresis counters, so the row could never escalate either.
   *
   * This is the same defect granola had (its toolkit is `native` too) and the
   * same fix, one layer up: granola was rescued by giving it an
   * `OAUTH_PROVIDERS.mcpUrl`, which a catalog-driven integration has no way to
   * do. Passing the catalog URL here routes them all to `mcp_tools_list`, which
   * `deriveMcpServerKey` already keys off, so they pick up a `.mcp.json` server
   * key and a real handshake without any further wiring.
   */
  remoteMcpUrl?: string | null;
  /**
   * ENG-5463 — the toolkit's `toolkit_definitions.cli_binary`, when set.
   *
   * Same shape of hole as `remoteMcpUrl` above, one lane over. Six seeded
   * toolkits name a CLI binary while carrying `source_type: 'native'`
   * (gcloud, xurl, greenlight, expo, and — once its row is corrected —
   * github), so they fell into the `native` case and resolved to `builtin`:
   * `unverified` forever, neutral to the hysteresis counters, unable to
   * escalate no matter how dead the credential.
   *
   * The value already travels from the catalog to the host — the runner reads
   * `integ.cli_binary` onto the target (connectivity-probe-runner.ts) and the
   * executor spends it on `runCli`. It simply never reached the resolver that
   * decides which probe to run, so the column that says "this is a CLI" had
   * no say in whether the CLI was asked.
   */
  cliBinary?: string | null;
}

/**
 * ENG-6042 / ENG-6428 — broker-managed toolkit ids → `cloud_account_enrolments.provider`
 * filter. These toolkits carry `auth_type='managed'` and `credentials {}` BY
 * DESIGN: the cloud-broker MCP mints scoped credentials per task at runtime, so
 * nothing is stored on the integration row and there is no static credential or
 * host-reachable endpoint to probe. The only meaningful signal is whether the
 * team/org actually has a broker enrolment, which is a CENTRAL check (see the
 * API's `testBrokerManagedToolkit`) — not a host-side reachability probe.
 *
 * `null` = no provider filter: `cloud-broker` is the shared broker MCP and serves
 * every provider the broker supports.
 *
 * Canonical here in core so the connectivity-probe resolver and the API Test path
 * read the SAME set — a drift between two copies is exactly what let broker
 * toolkits fall through to the Composio `managed_composite` probe and report a
 * perpetual false `transient_error` (ENG-6428). The API re-exports this.
 */
export const BROKER_TOOLKIT_PROVIDERS: Record<string, string | null> = {
  'aws-cli': 'aws',
  gcloud: 'gcp',
  'cloud-broker': null,
};

/** True when `definitionId` is a broker-managed toolkit (see {@link BROKER_TOOLKIT_PROVIDERS}). */
export function isBrokerToolkit(definitionId: string): boolean {
  return definitionId in BROKER_TOOLKIT_PROVIDERS;
}

/**
 * Approval brokers: `auth_type='managed'` and no host-probeable endpoint, but
 * NOT cloud-broker enrolments.
 *
 * These hold no credentials of their own — `xero-broker` authenticates to the
 * Augmented API with the host JWT and the team is derived server-side — so like
 * the cloud brokers above they have nothing a host-side probe can legitimately
 * reach. Without a short-circuit they fall into the `managed_composite` branch
 * and the host runs a Composio `tools/list` handshake against a server that does
 * not exist for them, failing every cycle into a perpetual false
 * `transient_error`. That is ENG-6428 happening a second time: an agent was
 * observed at 282 consecutive "failures" while a live `xero_preview_request`
 * against the same integration returned normally.
 *
 * They are deliberately kept OUT of {@link BROKER_TOOLKIT_PROVIDERS} because
 * that map drives `testBrokerManagedToolkit`, which validates a row in
 * `cloud_account_enrolments`. An approval broker has no cloud enrolment, so
 * adding it there would swap a false `transient_error` for an equally false
 * "no broker enrolment found" on the Test button. Same short-circuit, different
 * central check.
 */
export const APPROVAL_BROKER_TOOLKITS = new Set<string>(['xero-broker']);

/** True when `definitionId` is an approval broker (see {@link APPROVAL_BROKER_TOOLKITS}). */
export function isApprovalBrokerToolkit(definitionId: string): boolean {
  return APPROVAL_BROKER_TOOLKITS.has(definitionId);
}

/**
 * True when a `managed` toolkit has no host-reachable endpoint at all — either
 * a cloud broker (credentials minted per task) or an approval broker (no
 * credentials, server-side auth). Both must skip the host probe rather than
 * escalate a failure they are structurally incapable of passing.
 */
export function isHostUnprobeableBroker(definitionId: string): boolean {
  return isBrokerToolkit(definitionId) || isApprovalBrokerToolkit(definitionId);
}

/**
 * ENG-8801 — is this integration row DELIVERED centrally rather than through a
 * host-reachable per-integration endpoint? Two structural cases, both of which
 * the host connectivity probe cannot reach with a per-integration wired server,
 * so they honestly record `unverified`/`none` — and pre-8801 rendered a
 * misleading grey "unverified" health badge for a connection that works:
 *
 *  - `inherited` — the row is delivered by an ORG- or TEAM-scoped install. On an
 *    agent's Integrations tab such a connection has NO per-agent `.mcp.json`
 *    entry; its tools are served by the shared hosted MCP endpoint (ENG-4361),
 *    resolved dynamically at call time. Nothing per-integration exists on the
 *    host to probe.
 *  - a host-unprobeable broker toolkit (aws-cli / gcloud / cloud-broker /
 *    xero-broker) — no host-reachable endpoint by construction
 *    ({@link isHostUnprobeableBroker}).
 *
 * The badge uses this to render a distinct, non-green "managed centrally" state
 * INSTEAD of grey "unverified" for these rows. It is a PRESENTATION-only
 * distinction — never a new evidence tier, and never green: the `live_call`
 * gate (ENG-8226/8358) is untouched, so a genuinely dead central connection
 * still surfaces as degraded / reconnect-required, not "managed centrally".
 * Keeping the broker half wired to {@link isHostUnprobeableBroker} means the
 * badge state and the probe's "cannot reach this" set can't drift.
 */
export function isCentrallyManagedDelivery(args: {
  inherited?: boolean;
  definitionId?: string | null;
  definitionIds?: readonly string[] | null;
}): boolean {
  if (args.inherited) return true;
  if (args.definitionId && isHostUnprobeableBroker(args.definitionId)) return true;
  if (args.definitionIds?.some((id) => isHostUnprobeableBroker(id))) return true;
  return false;
}

/**
 * Definitions that are probed via a provider-specific read-only HTTP call by
 * the executors (the existing `testXConnection` helpers in the API route, and
 * the host-side equivalents). These are raw-token / direct-API providers, not
 * remote-MCP or Composio-managed.
 */
const HTTP_PROBE_PROVIDERS = new Set<string>([
  'linear',
  'google-workspace',
  'xero',
  'v0',
  // ENG-6642: Buffer is a direct-API GraphQL integration (api_key → Bearer),
  // not a CLI tool. Route it to the read-only org-details HTTP probe so health
  // reflects real API reachability, not a `buffer` binary on PATH. This wins
  // over its toolkit source_type, so the probe is honest regardless of how the
  // toolkit row is classified.
  'buffer',
  // LinkedIn Ads is a native OAuth + brokered-REST integration (Xero's shape),
  // so its honest health signal is the same: a central read-only check that the
  // stored token still authenticates. Routes to the OIDC userinfo probe in
  // connectivity-http-probes.ts.
  'linkedin-ads',
  // ENG-8422: Vercel is `source_type: 'native'`, so without this entry it falls
  // through to the `builtin` branch below — a hardcoded `unverified` verdict
  // having contacted nothing. `unverified` is neutral to hysteresis, so such an
  // integration can never report itself broken however bad its credential is.
  // That was tolerable while Vercel's auth was browser-OAuth brokered by Claude
  // Code (no server-held credential to check); ENG-8421 made it a
  // customer-supplied API token, at which point a revoked or mistyped token
  // became both the likeliest failure AND an invisible one. This entry is what
  // routes it to the real `api.vercel.com/v2/user` probe — `probeVercel` is
  // unreachable without it.
  'vercel',
  // ENG-8502: Higgsfield, for exactly the reasons given for Vercel above.
  // ENG-8440 made it a customer-supplied api_key but left it out of this set,
  // so it fell through to `builtin` and reported `unverified` having contacted
  // nothing. The consequence was worse than an uninformative chip: nothing
  // promotes an install off `status='configured'` without a real verdict, and
  // the console renders `configured` as "not connected" — so scout's WORKING
  // install (121 motion presets fetched through the broker) told the customer
  // it had never connected. Routes to `probeHiggsfield`: `Authorization: Key`
  // against the free, read-only `/v1/motions`.
  'higgsfield',
  // ENG-10082 (epic ENG-10081): Sprout Social, the third instance of the same
  // omission. `source_type: 'native'` with a null `cli_binary` — there is no
  // binary, the tools are brokered server-side against api.sproutsocial.com —
  // so it fell past every branch to `builtin` and returned `unverified` having
  // contacted nothing, forever.
  //
  // This is the install a customer reported (CS-1663 / ENG-9988). Basketball
  // Media saw a red "not connected" badge while the integration served six
  // successful authenticated round-trips across two agent sessions; an entire
  // commercial analysis was produced through it while the console said it was
  // down. `unverified` is neutral to hysteresis, so nothing promoted the row
  // off `status='configured'`, which the console renders as "not connected".
  //
  // Routes to `probeBearerJson` against the free, read-only, no-argument
  // `/v1/metadata/client` — the same read that backs `sprout_social_list_clients`.
  'sprout-social',
  // ENG-10524: Sportsyear, added WITH the integration rather than after an
  // incident, because the vendor's auth model makes the omission unusually
  // expensive. A Sportsyear API key never expires — it dies only when someone
  // removes or regenerates it — so there is no `expires_at` to watch and no
  // refresh to fail. Calling the API is the only way to learn a key has stopped
  // working, and without this entry nothing would ever call it: the toolkit is
  // `source_type: 'native'` and would fall to the `builtin` rubber-stamp. Worse
  // than Sprout's case, the integration is read-only, so a dead key shows up
  // as empty search results rather than a failed write.
  //
  // Routes to `probeSportsyear`: HTTP Basic (key as username, empty password)
  // against the free, read-only, no-argument `GET /api/v1/member`.
  'sportsyear',
  // ENG-10608: Instantly, added with the integration for the same reason as
  // Sportsyear. Its key is a customer-held, non-expiring API v2 key that dies
  // only when revoked, so only a call can tell a dead key from a live one.
  // Routes to `probeInstantly` against `GET /api/v2/accounts?limit=1`: free,
  // read-only, no required arguments. It needs `accounts:read`, which a
  // customer may leave off their key, so a 403 grades `degraded`, not `down`.
  'instantly',
  // ENG-6100: GitHub is deliberately NOT here. This set drives the ASYNC
  // connectivity monitor's routing, where github (source_type='native')
  // stays host-side (cli_command — `gh`, the credential the agent actually
  // executes with) rather than a central stored-token probe. The
  // synchronous Test button DOES probe the centrally-stored token via
  // `probeHttpProvider` (PROBE_DEFINITIONS in connectivity-http-probes.ts
  // includes 'github') — a narrower, honest "the token we stored is valid"
  // check. Unifying the monitor onto the central probe is sub-issue C's call.
]);

/**
 * Every definition this resolver routes to probe kind `http_provider`.
 *
 * Exported for the ENG-10095 parity guard. That guard asserts this set is a
 * SUBSET of `PROBE_DEFINITIONS` in `connectivity-http-probes.ts` — never equal
 * to it. The inequality is deliberate and load-bearing: `github` is in that set
 * and deliberately NOT here (ENG-6100), because the async monitor keeps github
 * host-side on the `gh` credential the agent actually executes with, while the
 * synchronous Test button probes the centrally-stored token. Asserting equality
 * would "fix" that on purpose.
 *
 * The subset direction is the one that bites: a member here with no entry there
 * — and so no `case` in `probeHttpProvider` — routes an HOURLY probe to `null`.
 */
export function listCentralHttpProbeProviders(): readonly string[] {
  return [...HTTP_PROBE_PROVIDERS];
}

/**
 * Native, registry-only integrations whose tools are served by a CLI-bundled
 * LOCAL STDIO MCP server (`~/.augmented/_mcp/<id>.js`), keyed by `definition_id`
 * because they have no `toolkit_definitions` row.
 *
 * ENG-8332 follow-up: `augmented-help-kb` now DOES have a seed row
 * (`toolkit-definitions.json`, `source_type: 'mcp_server'` +
 * `connectivity_test`), so this entry is no longer the only thing standing
 * between it and a dropped probe. It is kept deliberately, as the FALLBACK for
 * any environment whose catalog has not been seeded (a fresh local Supabase, a
 * host whose `/host/agent-integrations` join returned nothing). The two cannot
 * disagree: the catalog wins, because `mcpOverrideFrom(input.connectivityTest)`
 * is applied over the map's default below (`override.probeTool ?? bundled.tool`).
 * The seed row is what puts this id under the ENG-7405 probe-coverage ratchet
 * (`toolkit-connectivity-probe-coverage.test.ts` iterates the SEED, not this
 * map), so a future regression to a rubber-stamp kind now reddens `ci (22)`.
 *
 * Without an entry here they resolve on `sourceType`, and for a registry-only
 * integration `sourceType` is null (the API only fills it from
 * `toolkit_definitions`, host-runtime.ts ENG-5641). Null lands in the `default`
 * branch below: `unsupported` → a null outcome → the row is DROPPED from the
 * report batch. That is not a probe that fails, it is a probe that never runs,
 * and it is why every `augmented-support` install measured on the fleet still
 * carries `last_connectivity_check_at: null` having never been checked once
 * (12/12, 2026-08-02). ENG-8332 AC5 requires the help-KB split not to inherit
 * that.
 *
 * The value is the read-only tool to CALL after the handshake. A bare
 * initialize → tools/list would only prove the bundled server starts — the
 * ENG-8358 false-green shape — and would say nothing about whether the API
 * answers or the host JWT still authenticates. `search_knowledge_base` is a GET
 * with no side effects, and the MCP tool returns `isError: true` on any non-2xx,
 * so the open ENG-7260 `/host/kb/search` 500 that this ticket depends on
 * surfaces as `down` rather than as silence.
 *
 * Deliberately help-KB only for now. `augmented-support` / `augmented-admin`
 * have the same gap, but flipping ~12 live installs from invisible to reported
 * is a change with its own blast radius (first verdicts, first alerts) and
 * belongs in its own change, not smuggled into this split.
 */
const BUNDLED_STDIO_PROBE_TOOLKITS: Record<
  string,
  { tool: string; args: Record<string, unknown> }
> = {
  'augmented-help-kb': {
    tool: 'search_knowledge_base',
    args: { query: 'connectivity probe', limit: 1 },
  },
};

/** Read-only command args per CLI binary, keyed by `definition_id`. Default: `--version`. */
const CLI_PROBE_ARGS: Record<string, string[]> = {
  gcloud: ['version'],
  // ENG-6206: `gh --version` only proves the binary exists, not that it's
  // authenticated — so a missing/mis-named token read green (the false-green
  // that hid the broken fleet). `gh auth status` exits non-zero when not
  // logged in, the honest signal. Read-only. Requires the runner to pass the
  // agent's GH_TOKEN/GITHUB_TOKEN env (see manager-worker runCli wiring).
  github: ['auth', 'status'],
  // ENG-9244 — framer is deliberately ABSENT from this table, and must stay
  // absent. It has no credential-checking command that works without a project
  // URL: `project list` reads the local config file and never contacts Framer,
  // so it would pass with a garbage key, and `session new` needs a per-install
  // project URL this static table cannot supply. Adding ANY entry here would
  // flip `cliArgsAreAuthCheckFor` to true and grade the result `live_call` — the
  // one grade `evidenceSupportsOk` admits — manufacturing exactly the unearned
  // green the github entry above exists to remove. Left on the `--version`
  // default so the framework reports it honestly as binary-present /
  // credentials-unverified until a real probe exists.
  // most CLIs respond to --version; override here only when they don't.
};

/**
 * Resolve the CLI probe args: a stored `connectivity_test.args` (string[]) wins
 * over the per-binary default. ENG-6212. NB: CLI args run straight to the host
 * shell with no live tools/list to re-validate against (unlike MCP tools), so a
 * stored CLI override is guarded ONLY at seed-time by the CI seed-lint — it must
 * stay read-only there.
 */
function cliArgsFor(definitionId: string, ct?: ConnectivityTestOverride | null): string[] {
  if (Array.isArray(ct?.args) && ct.args.length > 0 && ct.args.every((a) => typeof a === 'string')) {
    return ct.args as string[];
  }
  return CLI_PROBE_ARGS[definitionId] ?? ['--version'];
}

/**
 * ENG-5463 — is the resolved CLI probe an actual credential check, or the
 * `--version` fallback?
 *
 * False for exactly one command — `['--version']`, which proves the binary is
 * installed and nothing about its credentials. True for anything else, whether
 * it came from an operator-stored `connectivity_test.args` override or from a
 * curated {@link CLI_PROBE_ARGS} entry (those exist precisely because
 * `--version` was not a real check for that binary).
 *
 * Graded off the RESOLVED args via `cliArgsFor`, not off which source supplied
 * them. Re-deriving the source precedence here instead let the two drift, and
 * they did: keying on "an override exists" graded a stored
 * `connectivity_test.args: ['--version']` as `live_call` — the one grade
 * `evidenceSupportsOk` admits — so a redundant override that restates the
 * default would have re-created the exact false green this flag exists to
 * prevent (CodeRabbit, PR #4453). Calling `cliArgsFor` makes the grade a
 * property of the command actually run, so the two cannot disagree by
 * construction.
 */
function cliArgsAreAuthCheckFor(
  definitionId: string,
  ct?: ConnectivityTestOverride | null,
): boolean {
  const args = cliArgsFor(definitionId, ct);
  return !(args.length === 1 && args[0] === '--version');
}

/**
 * Extract a managed/MCP override (tool name + object args) from the stored
 * `connectivity_test`. The tool is carried VERBATIM — the live read-only gate is
 * the probe (`resolveProbeTool`), not this pure resolver. ENG-6212.
 */
function mcpOverrideFrom(
  ct: ConnectivityTestOverride | null | undefined,
): { probeTool?: string; probeArgs?: Record<string, unknown> } {
  const tool = typeof ct?.tool === 'string' && ct.tool.trim().length > 0 ? ct.tool.trim() : undefined;
  if (!tool) return {};
  const args =
    ct?.args && typeof ct.args === 'object' && !Array.isArray(ct.args)
      ? (ct.args as Record<string, unknown>)
      : undefined;
  return { probeTool: tool, ...(args ? { probeArgs: args } : {}) };
}

/**
 * Resolve the connectivity probe strategy for an installed integration.
 *
 * Precedence is deliberate — `auth_type`/managed wins over `definition_id`,
 * which wins over remote-MCP, which wins over the raw `source_type` — so a
 * Composio-managed Linear install is probed as a Composio account (the real
 * signal), not as a raw Linear API call it has no key for.
 */
export function resolveConnectivityProbe(
  input: ConnectivityProbeInput,
): ConnectivityProbeDescriptor {
  const { definitionId, sourceType, authType } = input;

  // 0. Broker-managed toolkits (cloud-broker, aws-cli, gcloud) carry
  //    `auth_type='managed'` but are NOT Composio — the cloud-broker MCP mints
  //    scoped credentials per task at runtime, so there is no stored credential
  //    and no host-reachable endpoint to probe. Without this short-circuit they
  //    fell into the `managed_composite` branch below and the host probe ran a
  //    Composio MCP `tools/list` HTTP handshake against a server that doesn't
  //    exist for them — failing every cycle into a perpetual false
  //    `transient_error` (ENG-6428: 144 consecutive "failures" while the broker
  //    API was healthy). The honest host-side verdict is `unsupported`: skip,
  //    don't escalate. The real signal (broker enrolment presence) is a central
  //    check the API's `testBrokerManagedToolkit` already owns. We gate on
  //    `managed` so a non-broker install of the same definition_id (e.g. a raw
  //    OAuth `gcloud` CLI) keeps its normal source_type-based probe.
  //
  //    The same reasoning covers APPROVAL brokers (xero-broker): no stored
  //    credential, no host-reachable endpoint — the MCP child POSTs to the
  //    Augmented API with the host JWT. They are matched via
  //    `isHostUnprobeableBroker` rather than `isBrokerToolkit` so they skip the
  //    host probe WITHOUT being enrolled in the cloud-broker enrolment check.
  //
  //    ENG-8316: this used to return `unsupported`, which yields a null outcome
  //    and DROPS the row from the report batch. That silenced the false
  //    transient_error as intended, but it also cut these rows off from
  //    `applyConnectivityReports` — the one place their stale state is cleared
  //    (ENG-8205) — so the old verdict froze in place instead of clearing. The
  //    honest verdict is still "we did not check", but it has to be REPORTED as
  //    `unverified` rather than withheld. Same zero network calls; the row just
  //    stops being invisible.
  if (isHostUnprobeableBroker(definitionId) && (authType === 'managed' || sourceType === 'managed')) {
    return {
      kind: 'host_unprobeable',
      sourceType: 'managed',
      readOnly: true,
      label: isApprovalBrokerToolkit(definitionId)
        ? `${definitionId}: approval broker — no stored credential; writes authenticate server-side (no host probe)`
        : `${definitionId}: broker-managed — credentials minted per task; no host probe (central enrolment check only)`,
      centralReachable: false,
    };
  }

  // 1. Managed (Composio) toolkits are wired as remote MCP servers in the
  //    agent's .mcp.json (`composio_<toolkit>`), so the HONEST connectivity
  //    test is a host-side `tools/list` handshake against that server with the
  //    agent's injected token — exactly what a host probe is for. (ENG-5665)
  //
    //    ENG-6139: the MCP handshake alone is NOT sufficient. Composio returns a
    //    toolkit's tool list even when no connected account exists for the
    //    entity — only tool *calls* fail (`No connected account found for user
    //    id …`). So a dead/mis-bound account reads green on `tools/list` (the
    //    live sherlock incident). We now run BOTH host-side — the handshake
    //    (network / token-injection / MCP-URL drift, ENG-5665's valid point)
    //    AND the connected-account binding check — and take the worse outcome.
    //    The executor resolves the `composio_<toolkit>` server key from the
    //    target's `mcpServerKey` and the account inputs from its `credentials`;
    //    a missing capability degrades gracefully (never a false `down`).
  if (authType === 'managed' || sourceType === 'managed') {
    return {
      kind: 'managed_composite',
      sourceType: 'managed',
      readOnly: true,
      label: `${definitionId}: MCP tools/list + account binding (managed)`,
      centralReachable: false,
      // ENG-6212: carry the operator-stored override tool (if any) so both the
      // host executor and the central Test path call the same specific tool.
      ...mcpOverrideFrom(input.connectivityTest),
    };
  }

  // 2. Known raw-token / direct-API OAuth providers.
  if (HTTP_PROBE_PROVIDERS.has(definitionId)) {
    return {
      kind: 'http_provider',
      sourceType: sourceType ?? 'mcp_server',
      readOnly: true,
      label: `${definitionId}: read-only API check`,
      centralReachable: true,
      httpProvider: definitionId,
    };
  }

  // 2b. Native, registry-only integrations served by a CLI-bundled local-stdio
  //     MCP server. Keyed on `definition_id` for the same reason step 2 is:
  //     they have no `toolkit_definitions` row, so there is no `source_type` for
  //     step 4 to switch on and they would fall through to `unsupported` and be
  //     dropped from the report entirely (see BUNDLED_STDIO_PROBE_TOOLKITS).
  //     Placed here so it keeps the documented precedence — definition_id beats
  //     remote-MCP, which beats raw source_type.
  const bundledStdio = BUNDLED_STDIO_PROBE_TOOLKITS[definitionId];
  if (bundledStdio) {
    // An operator-stored `connectivity_test` override still wins, exactly as it
    // does for every other stdio toolkit. When it names a different tool its own
    // args go with it — we must not staple the built-in default's args onto
    // someone else's tool.
    const override = mcpOverrideFrom(input.connectivityTest);
    const probeTool = override.probeTool ?? bundledStdio.tool;
    const probeArgs = override.probeTool ? override.probeArgs : bundledStdio.args;
    return {
      kind: 'mcp_stdio',
      sourceType: 'mcp_server',
      readOnly: true,
      label: `${definitionId}: local-stdio MCP handshake + ${probeTool}`,
      centralReachable: false,
      probeTool,
      ...(probeArgs ? { probeArgs } : {}),
    };
  }

  // 3. Remote streamable-HTTP MCP providers (granola, …) — probe via an
  //    `initialize → tools/list` handshake against the provider's public MCP URL.
  //
  //    ENG-6396: this IS centrally reachable. Unlike Composio (where the bearer
  //    is injected on the host and the URL carries a host-resolved user_id),
  //    these providers store their OAuth `access_token` in the integration's
  //    `credentials` — exactly what the central control plane reads — and the
  //    `mcpUrl` is a fixed public endpoint. So the API's synchronous Test path
  //    can run the same `tools/list` probe with `Authorization: Bearer
  //    <access_token>`. Before this, the central path had no probe for these,
  //    fell through to a bare "Credentials present" non-verification, and a
  //    server-side-expired token sat looking healthy until the next live
  //    `tools/call` threw (the granola/Dwight incident). The host probe still
  //    runs this kind too (it runs every kind regardless of `centralReachable`).
  //    ENG-7077: the catalog `remote_mcp.url` counts as a remote MCP too. See
  //    {@link ConnectivityProbeInput.remoteMcpUrl} for why the OAUTH_PROVIDERS
  //    lookup alone left every ADR-0033 static-token remote MCP unprobed.
  //
  //    `centralReachable` deliberately splits on WHICH of the two matched. The
  //    registry providers store an OAuth `access_token` in `credentials`, which
  //    is what the central Test path reads and sends as `Authorization: Bearer`.
  //    A catalog remote MCP instead carries a structured `RemoteMcpAuth`
  //    (`scheme` + `credential_ref`, ADR-0033 C1) that the central path has no
  //    builder for yet, so claiming central reachability here would hand the
  //    synchronous Test an unauthenticated handshake and a 401 it would report
  //    as the integration being down. The HOST probe runs every kind regardless
  //    of this flag, and the host reads the real wired server out of the agent's
  //    own `.mcp.json` — which is the probe that actually matters for the fleet
  //    counters. Central reachability for the catalog lane is the follow-up.
  const remoteMcpUrl = getOAuthProvider(definitionId)?.mcpUrl ?? input.remoteMcpUrl ?? null;
  if (remoteMcpUrl) {
    const fromRegistry = Boolean(getOAuthProvider(definitionId)?.mcpUrl);
    // ENG-7077 C7 seam: carry the toolkit's `connectivity_test` through, exactly
    // as the managed and stdio branches already do. Without it this lane could
    // only ever HANDSHAKE, and a handshake cannot earn a green badge —
    // `evidenceSupportsOk` admits `live_call` alone, so `deriveIntegrationHealth`
    // downgrades a perfectly healthy remote MCP to `unverified`
    // (`connectivity:ok-without-live-call:handshake`).
    //
    // That is the half of ENG-7077 the probe fix (#4419) did not reach. It made
    // the DOWN case work — a dead token now escalates instead of reporting
    // `unverified` forever — but the OK case still could not reach green, for a
    // different reason. An operator would see slate "nobody has confirmed this"
    // on an integration that is working, which is a quieter version of the same
    // fault: a health surface that cannot say what it actually knows.
    //
    // `probeMcpHttp` has supported the read-only `tools/call` since ENG-6957 and
    // `McpProbeTarget` already carries `toolName`/`toolArgs`. The capability was
    // present end to end; only this lane never passed one.
    const override = mcpOverrideFrom(input.connectivityTest);
    return {
      kind: 'mcp_tools_list',
      sourceType: sourceType ?? 'mcp_server',
      readOnly: true,
      label: `${definitionId}: MCP tools/list${override.probeTool ? ` + ${override.probeTool}` : ''}`,
      centralReachable: fromRegistry,
      ...override,
    };
  }

  // 4. Fall back to the toolkit source_type.
  switch (sourceType) {
    case 'mcp_server':
      // ENG-5677/ENG-7405: step 3 already routed remote streamable-HTTP MCP
      // servers (those with a registered `mcpUrl`) to `mcp_tools_list`.
      // Reaching this branch means `source_type='mcp_server'` with NO remote
      // URL — a local-STDIO MCP server (origami, and any bundled/spawned
      // stdio toolkit). ENG-7405 replaced the old `unsupported` no-op with a
      // real `mcp_stdio` probe: the manager spawns the toolkit's bundled
      // server with the agent's env and runs initialize → tools/list (→ the
      // read-only `connectivity_test` tool when set). The override is carried
      // through so a stdio toolkit that names a free read (origami
      // `get_credit_balance`) gets a real execute-level check, not just a
      // handshake.
      return {
        kind: 'mcp_stdio',
        sourceType: 'mcp_server',
        readOnly: true,
        label: `${definitionId}: local-stdio MCP handshake${input.connectivityTest?.tool ? ` + ${input.connectivityTest.tool}` : ''}`,
        centralReachable: false,
        ...mcpOverrideFrom(input.connectivityTest),
      };
    case 'cli_tool':
      return {
        kind: 'cli_command',
        sourceType: 'cli_tool',
        readOnly: true,
        label: `${definitionId}: CLI reachability`,
        centralReachable: false,
        cliArgs: cliArgsFor(definitionId, input.connectivityTest),
        cliArgsAreAuthCheck: cliArgsAreAuthCheckFor(definitionId, input.connectivityTest),
      };
    case 'native':
      // ENG-5463: a toolkit seeded `native` that nonetheless names a CLI
      // binary IS driven by that CLI, and the binary is a real health signal.
      // Routing it to `builtin` reports `unverified` having contacted
      // nothing — and `unverified` is NEUTRAL to both hysteresis counters
      // (integration-connectivity-report.ts), so the row can never escalate.
      // A revoked credential then emits byte-for-byte what a live one emits,
      // for as long as nobody looks. github sat in exactly that state.
      //
      // The intent was already written down twice and never reachable:
      // `CLI_PROBE_ARGS` has carried `github: ['auth','status']` since
      // ENG-6206 (with a comment explaining why `gh --version` is a false
      // green), and the ENG-6100 note on `HTTP_PROBE_PROVIDERS` above says
      // github "stays host-side (cli_command — `gh`, the credential the agent
      // actually executes with)". Both were dead: the seed row said
      // `source_type: 'native'`, so this switch sent it to `builtin`.
      //
      // Keying on `cli_binary` rather than the code registry follows the
      // precedent already shipped in `integration-catalog.ts`, which
      // classifies a toolkit as CLI on `cli_package || cli_binary ||
      // source_type === 'cli_tool'` — i.e. the column, not the source_type,
      // is what says "this is a CLI". The row already carries it to the host
      // (connectivity-probe-runner.ts), it simply never reached the resolver.
      if (input.cliBinary) {
        return {
          kind: 'cli_command',
          sourceType: 'native',
          readOnly: true,
          label: `${definitionId}: CLI reachability (${input.cliBinary})`,
          centralReachable: false,
          cliArgs: cliArgsFor(definitionId, input.connectivityTest),
          cliArgsAreAuthCheck: cliArgsAreAuthCheckFor(definitionId, input.connectivityTest),
        };
      }
      return {
        kind: 'builtin',
        sourceType: 'native',
        readOnly: true,
        label: `${definitionId}: built-in check`,
        centralReachable: false,
      };
    default:
      return {
        kind: 'unsupported',
        sourceType: sourceType ?? 'native',
        readOnly: true,
        label: `${definitionId}: no connectivity probe available`,
        centralReachable: false,
      };
  }
}

/** True when the API control plane can run this probe itself (vs. host-only). */
export function isCentrallyProbeable(descriptor: ConnectivityProbeDescriptor): boolean {
  // ENG-8316: `host_unprobeable` is excluded explicitly rather than relying on
  // its `centralReachable: false`. The exclusion is a statement about the kind,
  // not about one descriptor's flag — a broker's real check is the central
  // `testBrokerManagedToolkit` enrolment path, not this probe — so it should not
  // become true if someone later flips that flag for an unrelated reason.
  return (
    descriptor.centralReachable &&
    descriptor.kind !== 'unsupported' &&
    descriptor.kind !== 'host_unprobeable'
  );
}
